import { Box, Typography, Paper, Button, TextField, InputAdornment, Snackbar, Alert } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { useApiClient } from "../useApiClient";
import { API_CONFIG, getApiUrl, NAVIGATION_CONFIG } from "../config/api";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { Phone } from "../types/phone";
import { borderRadius, colors, elevation } from '../theme';

interface CareHome {
  externalId: string;
  name: string;
  contactPerson?: string | null;
  contactEmail?: string | null;
  phones: Phone[];
}

export default function CareHomes() {
  // Search state for each field
  const [searchFields, setSearchFields] = useState({
    name: "",
    contactPerson: "",
    email: "",
    phone: "",
    postcode: "",
  });

  // Helper to reset all except one field
  const handleSearchFieldChange = (field: string, value: string) => {
    setSearchFields((prev) => {
      const resetFields = { name: "", contactPerson: "", email: "", phone: "", postcode: ""};
      return { ...resetFields, [field]: value };
    });
  };

  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error" | "warning">("success");

  // Toast helper method
  const showToast = (message: string, severity: "success" | "error" | "warning" = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  // Clear filter handler
  const handleClearFilters = () => {
    setSearchFields({ name: "", contactPerson: "", email: "", phone: "", postcode: "" });
    setActiveFilter(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const { callApi } = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [careHomes, setCareHomes] = useState<CareHome[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track active filter for server
  const [activeFilter, setActiveFilter] = useState<{
    field: string;
    value: string;
  } | null>(null);

  // Handle search button click
  const handleSearch = () => {
    const field = Object.keys(searchFields).find(
      (key) => searchFields[key as keyof typeof searchFields]
    );
    if (!field) {
      setActiveFilter(null);
      return;
    }

    // Check if the search value has at least 3 characters
    const searchValue = searchFields[field as keyof typeof searchFields];
    if (searchValue.length < 3) {
      showToast("Please enter at least 3 characters to search", "warning");
      return;
    }

    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page
    setActiveFilter({
      field,
      value: searchFields[field as keyof typeof searchFields],
    });
  };

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    () => {
      if (location.state?.returnedFromDetails) {
        return {
          page: location.state.page,
          pageSize: location.state.pageSize,
        };
      }
      return { page: 0, pageSize: 10 };
    }
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const fetchCareHomes = async (
    page: number, 
    pageSize: number,
    sortModel: GridSortModel
  ) => {
    try {
      setLoading(true);
      
      // Build sort query string
      let sortQuery = "";
      if (sortModel && sortModel.length > 0) {
        const sort = sortModel[0];
        sortQuery = `&sortBy=${sort.field}&sortDirection=${sort.sort}`;
      }

      let filterQuery = "";
      let filterField = "";
      if (activeFilter && activeFilter.value) {
        // Map field to server param
        if (activeFilter.field === "name") filterField = "name";
        if (activeFilter.field === "contactPerson") filterField = "contactPerson";
        if (activeFilter.field === "email") filterField = "contactEmail";
        if (activeFilter.field === "phone") filterField = "phones";
        if (activeFilter.field === "postcode") filterField = "postcode";

        filterQuery = `&filterBy=${filterField}&filterValue=${encodeURIComponent(
          activeFilter.value
        )}`;
      }

      const result = await callApi(
        `${getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES)}?page=${
          page + 1
        }&pageSize=${pageSize}${sortQuery}${filterQuery}`
      );
      setCareHomes(result.data.items);
      setTotalRows(result.data.totalCount);
      setError(null);
    } catch (err: any) {
      console.error("API call failed:", err);
      if (err.response?.status === 403) {
        setError("Access denied: You do not have permission.");
      } else {
        setError("An error occurred while fetching shared address data.");
      }
      setCareHomes([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCareHomes(
      paginationModel.page,
      paginationModel.pageSize,
      sortModel
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, sortModel, activeFilter]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleAddNew = () => {
    navigate(NAVIGATION_CONFIG.ROUTES.CARE_HOMES_NEW);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "contactPerson",
      headerName: "Contact Person",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "contactEmail",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "phones",
      headerName: "Phone",
      flex: 1,
      minWidth: 150,
      valueGetter: (phones: Phone[]) => {
        if (!phones.length) return "";
        const primary = phones.find((p: any) => p.isPrimary) || phones[0];
        if (primary && typeof primary.number === "string") {
          try {
            const phoneObj = parsePhoneNumberFromString(primary.number, "GB");
            return phoneObj ? phoneObj.formatInternational() : primary.number;
          } catch {
            return primary.number;
          }
        }
        return "";
      },
    },
  ];

  return (
    <Box sx={{ pl: 3 }}>
      {/* Top card for title and add button inside Paper */}
      <Paper
        sx={{
          mb: 3,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flexWrap: "wrap",
        }}
        elevation={5}
      >
        {/* First line: title and add button */}
        <Box
          sx={{ display: "flex", alignItems: "center", width: "100%", mb: 1 }}
        >
          <Typography variant="h4" sx={{ flex: 1 }}>
            Shared Addresses
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ borderRadius: borderRadius.mui.medium }}
          >
            Add Shared Address
          </Button>
        </Box>
        {/* Second line: filters */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            variant="outlined"
            placeholder="Name"
            value={searchFields.name}
            onChange={(e) => handleSearchFieldChange("name", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />

          <TextField
            variant="outlined"
            placeholder="Contact Person"
            value={searchFields.contactPerson}
            onChange={(e) => handleSearchFieldChange("contactPerson", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />

          <TextField
            variant="outlined"
            placeholder="Email"
            value={searchFields.email}
            onChange={(e) => handleSearchFieldChange("email", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />

          <TextField
            variant="outlined"
            placeholder="Phone"
            value={searchFields.phone}
            onChange={(e) => handleSearchFieldChange("phone", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <PhoneIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />

          <TextField
            variant="outlined"
            placeholder="Postcode"
            value={searchFields.postcode}
            onChange={(e) =>
              handleSearchFieldChange("postcode", e.target.value)
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LocationOnIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 160 }}
          />

          <Tooltip title="Search">
            <IconButton
              aria-label="Search"
              onClick={handleSearch}
              color="primary"
              sx={{ ml: 1 }}
              size="large"
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reset filters">
            <IconButton
              aria-label="Clear filters"
              onClick={handleClearFilters}
              sx={{ ml: 1 }}
              size="large"
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Main grid without Paper, with shadow */}
      {error ? (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            minHeight: 400,
            boxShadow: elevation.low,
            borderRadius: borderRadius.mui.medium,
            background: colors.background.sidebarGlass,
          }}
        >
          <DataGrid
            rows={careHomes}
            columns={columns}
            getRowId={(row) => row.externalId}
            paginationMode="server"
            sortingMode="server"
            pageSizeOptions={[5, 10, 25]}
            paginationModel={paginationModel}
            rowCount={totalRows}
            loading={loading}
            onPaginationModelChange={handlePaginationModelChange}
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            onRowClick={(params) =>
              navigate(NAVIGATION_CONFIG.getCareHomeDetailRoute(params.row.externalId), {
                state: {
                  fromPage: paginationModel.page,
                  pageSize: paginationModel.pageSize,
                },
              })
            }
            sx={{
              border: 0,
              "& .MuiDataGrid-row": {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              },
              "& .MuiDataGrid-main": {
                borderRadius: 0,
              },
              "& .MuiDataGrid-columnHeaders": {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
              "& .MuiDataGrid-footerContainer": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
            }}
          />
        </Box>
      )}

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setToastOpen(false)} 
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
